import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormSetValue, UseFormUnregister } from 'react-hook-form';

import type { Props as PlainDatePickerProps } from 'af-components/Controls/DateInput';

import DatePickerCell from '../../RHFTableCellComponents/DatePicker/DatePickerCell';
import type { RenderMethodParams } from '../RHFFieldCellWrapper';
import RHFFieldWrapper from '../RHFFieldCellWrapper';

type Props<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
> = {
	rhfId: number | string;
	onChange: (name: string) => void;
	onBlur: () => void;
	unregister: UseFormUnregister<TFieldValues>;
	setValue: UseFormSetValue<TFieldValues>;
	openFocused?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDatePickerProps;

function DatePickerField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, onChange, onBlur, setValue, openFocused = false, ...rest } = props;

	const renderDatePickerCell = React.useCallback((
		{
			field,
			error,
			onFieldChange,
			onFieldBlur,
		}: RenderMethodParams<TFieldValues, TName>
	) => (
		<DatePickerCell
			{...rest}
			autoOpen={openFocused}
			elementKey={rhfId}
			error={error ?? undefined}
			key={name + rhfId}
			name={field.name}
			onBlur={onFieldBlur}
			onChange={onFieldChange}
			placeholder={placeholder}
			setValue={setValue}
			value={field.value}
		/>
	), [rhfId, name, placeholder, rest, openFocused, setValue]);

	return (
		<RHFFieldWrapper
			{...rest}
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			render={renderDatePickerCell}
		/>
	);
}

export default React.memo(DatePickerField) as typeof DatePickerField;
